import React, { useState } from 'react';
import { Modal } from './Modals';
import { Button } from './UnifiedUIComponents';
import { FaCopy, FaFilePdf, FaFileWord, FaRobot } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { exportAIDocToPdf, exportAIDocToWord } from '../utils/aiDocExport';
import { useAppSettings } from '../context/AppSettingsContext';

// Shared in-page result view used by every page-wise AI action (draft protocol/report, risk review,
// data review, grouping suggestions). Always advisory — never auto-saves anything.
// `docType` picks the export template: 'protocol' | 'report' get a company letterhead,
// document-control header and a formal Prepared/Reviewed/Approved signature table;
// 'note' (default) gets the letterhead but a lighter single-line review stamp.
const AIResultPanel = ({ isOpen, onClose, title, loading, error, content, groups, onUseGroup, exportFileName, docType = 'note' }) => {
  const { appName } = useAppSettings();
  const [exporting, setExporting] = useState(null);
  if (!isOpen) return null;

  const baseFileName = (exportFileName || 'ai-output.pdf').replace(/\.(pdf|docx)$/i, '');

  const handleCopy = () => {
    navigator.clipboard.writeText(content || '').then(() => toast.success('Copied to clipboard'));
  };

  const handleExportPdf = async () => {
    setExporting('pdf');
    try {
      await exportAIDocToPdf(title, content, `${baseFileName}.pdf`, docType, appName);
    } catch (err) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(null);
    }
  };

  const handleExportWord = async () => {
    setExporting('docx');
    try {
      await exportAIDocToWord(title, content, `${baseFileName}.docx`, docType, appName);
    } catch (err) {
      toast.error('Failed to export Word document');
    } finally {
      setExporting(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={<span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FaRobot /> {title}</span>}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!loading && content && (
            <>
              <Button variant="outline" onClick={handleCopy}><FaCopy style={{ marginRight: 6 }} />Copy</Button>
              <Button variant="outline" onClick={handleExportWord} disabled={!!exporting}>
                <FaFileWord style={{ marginRight: 6 }} />{exporting === 'docx' ? 'Exporting…' : 'Export Word'}
              </Button>
              <Button variant="primary" onClick={handleExportPdf} disabled={!!exporting}>
                <FaFilePdf style={{ marginRight: 6 }} />{exporting === 'pdf' ? 'Exporting…' : 'Export PDF'}
              </Button>
            </>
          )}
        </>
      }
    >
      {loading && (
        <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Thinking… this can take up to a minute depending on the provider.
        </div>
      )}

      {!loading && error && (
        <div style={{ padding: '1rem', borderRadius: 'var(--radius)', background: 'var(--danger-bg)', color: 'var(--danger-text)', border: '1px solid var(--danger-border)' }}>
          {error}
        </div>
      )}

      {!loading && !error && groups && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {groups.map((g, i) => (
            <div key={i} style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius)', padding: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                <strong>{g.group_name}</strong>
                {onUseGroup && (
                  <Button variant="outline" size="sm" onClick={() => onUseGroup(g)}>Use this group</Button>
                )}
              </div>
              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>{g.reasoning}</div>
              <div style={{ fontSize: 'var(--fs-md)' }}>Members: {(g.member_ids || []).join(', ')}</div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && !groups && (
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 'var(--fs-base)', color: 'var(--text-primary)', maxHeight: '60vh', overflowY: 'auto' }}>
          {content}
        </div>
      )}
    </Modal>
  );
};

export default AIResultPanel;
